import * as React from 'react';
import {Timepicker} from 'react-timepicker';
import 'react-timepicker/timepicker.css';
import { HotKeys } from 'react-hotkeys';
import { DateTimeProps } from '../date-time-props';
import { FaRegClock } from 'react-icons/fa';
import { ButtonInputGroup, ButtonPrimary, Validation, ValidationLevel } from '../../common';
import { timeParser  } from './parse-time';
import { timeFormatter } from 'ui/date-time/time/format-time';
import isDate from 'lodash/isdate';
import { Triangle } from 'ui';
import styles from './time.scss';
import { ANIMATE_OUT_SPEED } from 'ui/constants';
import { OnClickOutside } from 'ui/common/on-click-outside';

interface TimePickerState{
    selectedHours?: number;
    selectedHours24?: number;
    selectedMinutes?: number;
    value?: string;
    showClock?: boolean;
    isClosing: boolean;
    selectedMeridian?: string;   //TODO: check if it can be removed. Should be a formatting concern
    selectHours?: boolean; // used to force overriding the internal hours/minutes state of the ugly time picker
    emptyDateError?: boolean;
}

export class TimePicker extends React.Component <DateTimeProps, TimePickerState> {
  private meridianAm: string;
  private meridianPm: string;
  private keyMap: any = { escapeKey: ['esc'], enterKey: ['enter'] };
  private use12HrTime: boolean;
  private parser: any; //TODO: not gonna type this before next refactor
  private formatter: any;
  /**
   * Creates a Time component with default state.
   *
   */
  constructor(props: DateTimeProps) {
    super(props);

    // TODO: fix the double timeParser initialize
    this.parser = timeParser(props.locale, false, props.formatDate, props.createDate);

    this.meridianAm = this.parser.getAmPmSuffixFromCurrentLocaleIfAny().am; //no need for this here, todo: this.parser.amSuffix
    this.meridianPm = this.parser.getAmPmSuffixFromCurrentLocaleIfAny().pm; //no need for this here, todo: this.parser.pmSuffix
    this.use12HrTime = !!(this.meridianAm || this.meridianPm);  // todo: this.use12hour = this.parser.currentLocaleIs12hour
    this.parser = timeParser(props.locale, this.use12HrTime, props.formatDate, props.createDate);

    this.formatter = timeFormatter(props.locale, this.use12HrTime, props.createDate, props.formatDate );
    this.state = this.calculateInitialOrUpdatedState(props.value);
  }

  public UNSAFE_componentWillReceiveProps(props: DateTimeProps) {
    const newValue = isDate(props.value) ? props.value.getTime() : undefined;
    const oldValue = isDate(this.props.value) ? this.props.value.getTime() : undefined;
    if(!newValue) {
      this.setState({value: ''});
      return;
    }
    const valueDidChange = !!newValue && newValue !== oldValue;
    if (valueDidChange) {
      this.setState(this.calculateInitialOrUpdatedState(props.value));
    }
  }

  private calculateInitialOrUpdatedState = (valueProp: Date) => {  
    let hours24; let minutes; let meridian; let valueInCurrentLocale;

    if (valueProp instanceof Date) {
      minutes = valueProp.getUTCMinutes();
      hours24 = valueProp.getUTCHours();
      valueInCurrentLocale = this.getDisplayValue(hours24, minutes);
      meridian = this.parser.getAmPmSuffixesFromUserInput(hours24 + ':' + minutes);
    }
    const hours = this.use12HrTime ? this.parser.convert24To12(hours24) : hours24;
    return {
      selectedHours: hours,
      selectedHours24: hours24,
      selectedMinutes: minutes,
      value: valueInCurrentLocale,
      showClock: false,
      selectedMeridian: meridian,
      isClosing: false,
      emptyDateError: false
    };
  };

  private getDisplayValue = (hours: number, minutes: number) => {
     return this.formatter.formatTime(hours, minutes);
  };

  private update = (hours: number, minutes: number, meridiem: string, additionalState: Partial<TimePickerState> = {}) => {
    const hours24 = this.parser.convert12To24(hours, minutes, meridiem);
    const timeHasChanged = hours24 !== this.state.selectedHours24 || minutes !== this.state.selectedMinutes;

    this.setState({
      selectedHours: hours,
      selectedHours24: hours24,
      selectedMinutes: minutes,
      selectedMeridian: meridiem,
      value: this.getDisplayValue(hours24, minutes),
      isClosing: false,
      ...additionalState
    }, () => {
      if (timeHasChanged){
        this.fireOnChangeCallback(hours24, minutes);
      }
    });
  };

  private resetState = () => {
    this.setState({ value: '',
      selectedHours: 0,
      selectedHours24: 0,
      selectedMinutes: 0,
      selectedMeridian: ''
    });
    this.fireOnChangeCallback(0, 0);
  };

  /**
   * @private Handles blur event for time input.
   *
   */
  private handleInputBlur = () => {
    const { defaultValue } = this.props;
    const value: string = this.state.value || defaultValue;

    // clear
    if (value === '' && this.state.selectedHours){
      this.fireOnChangeCallback();
      return;
    }

    // TODO: cleanup all below
    // use array destruct
    const parsedTimeInput = this.parser.parseTimeInputOrUserShortcut(value);
    if (parsedTimeInput) {
      const meridian = parsedTimeInput[2];
      const minutes = parsedTimeInput[1];
      const hours =  !this.use12HrTime ? this.parser.convert12To24(parsedTimeInput[0], minutes, meridian) : parsedTimeInput[0];
      const parsedTimeIsValid = hours !== undefined && minutes !== undefined;
      if (parsedTimeIsValid){
        const valueDidChange = parsedTimeIsValid && (hours !== this.state.selectedHours ||
        minutes !== this.state.selectedMinutes ||
        !!meridian && meridian !== this.state.selectedMeridian);
        if (valueDidChange) {
          this.update(hours, minutes, meridian);
        }
        else {
          this.update(this.state.selectedHours24, this.state.selectedMinutes, this.state.selectedMeridian);
        }

      }else{
        this.resetState();
      }
    }else{
      this.resetState();
    }
  };

  // this should not take arguments, it's inconsistent to not always use the current state when firing
  // external change
  private fireOnChangeCallback(hours?: number, minutes?: number) {
    const lastPropValue = this.props.value;
    
    if (lastPropValue && hours !== undefined && minutes !== undefined){
      lastPropValue.setUTCHours(hours);
      lastPropValue.setUTCMinutes(minutes);
      this.props.onChange(lastPropValue);
    }
  }

  private handleInputChange = (e: React.SyntheticEvent<any>) => {
    //TODO: handle like it's handled in Date.tsx.. postponed
    if(!this.props.value) {
      this.setState({emptyDateError: true});
      return;
    }
    const { value } = e.target as HTMLInputElement;
    this.setState({ value, emptyDateError: false });
  };

  private handleChange = (hours: number, minutes: number) => {
    //TODO: handle input and parsing like it's handled in Date.tsx.. postponed
    if (hours !== this.state.selectedHours || minutes !== this.state.selectedMinutes){
      this.update(hours, minutes, this.state.selectedMeridian);
    }
  };

  private switchToAm = () => {
    this.update(this.state.selectedHours, this.state.selectedMinutes, this.meridianAm);
  };

  private switchToPm = () => {
    this.update(this.state.selectedHours, this.state.selectedMinutes, this.meridianPm);
  };

  private handlerHotkeys: any = { escapeKey: () => this.setState({ showClock: false }) };

  private closePicker = () => {
    if (this.state.showClock){

      this.setState({ isClosing: true });

      setTimeout(() => {
        this.setState({
          showClock: false,
          isClosing: false
        });
      }, ANIMATE_OUT_SPEED);
    }
    this.setState({emptyDateError: false});
  };

  private onFocus = () => {
    //TODO: handle like it's handled in Date.tsx.. postponed
    this.closePicker();
  };

  private togglePicker = () => {
    if (this.state.showClock) {
      this.closePicker();
    } else {
      if(!this.props.value) {
        this.setState({emptyDateError: true});
        return;
      }
      this.setState({
        showClock: !this.state.showClock,
        emptyDateError: false
      });
    }

    this.setState({
      selectHours: true
    });
  };

  private handleModeChange = (mode: boolean) => {
     // when we are in minutes mode, we want wait 10ms,
     //  then override 3rdparty internal state, caused by internal click events
    setTimeout(() => {
      this.setState({selectHours: this.state.selectHours ? mode : false });
    }, 10);
  };

  /**
   * @private Handles parsing on pressing 'Enter' key
   *
   */
  private onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if(event.key === 'Enter') {
       this.handleInputBlur();
    }    
    this.onFocus();
  };

  public render() {

    if (this.props.readOnly){
      return (
        <div className={`input-wrapper ${styles.wrapper}`}>
          <div className="input-group form-control-padding">{this.state.value || '\u00A0'}</div>
        </div>
      );
    }

    const meridian = this.state.selectedMeridian;

    // eslint-disable-next-line max-len
    const amButton = <button className={(meridian === this.meridianAm ? styles.am : styles.amDefault)} type="button" onClick={this.switchToAm}>{this.meridianAm}</button>;
    // eslint-disable-next-line max-len
    const pmButton = <button className={meridian === this.meridianPm ? styles.pm : styles.pmDefault}   type="button" onClick={this.switchToPm}>{this.meridianPm}</button>;

    const doneButton =  <div className={styles.doneButtonWrapper}>
                          <ButtonPrimary style={styles.doneBtn} onClick={this.closePicker}>
                            {this.props.labelConfirmTimeSelection}
                          </ButtonPrimary>
                        </div>;

    const am = this.use12HrTime && amButton;
    const pm = this.use12HrTime && pmButton;

    const clockPicker = (<div className={styles.pickerWrapper}>
      <div className={`${styles.clockWrapper}`}>
        {am && amButton}
        {pm && pmButton}
        <Timepicker
          className={this.props.className}
          onChange={this.handleChange}
          onChangeMode={this.handleModeChange}
          hours={this.state.selectedHours}
          minutes={this.state.selectedMinutes}
          militaryTime={!this.use12HrTime}
          mode={this.state.selectHours}
          formatNumber={this.formatter.formatTimePicker}
        />
        {doneButton}
      </div>
    </div>);

    const timeIconOnClick = !this.props.readOnly ? this.togglePicker : undefined;

    const placeholderTime = !this.state.selectedMeridian ? this.props.placeholderTime24hr : this.props.placeholderTime12hr;

    const closingAnimation = this.state.isClosing ? 'animate-out' : '';

    const errorParams = {
      level: ValidationLevel.error,
      messages: [{level: ValidationLevel.error, message: `${this.props.textEmptyDateError}`}]
    };

    return (
      <OnClickOutside onClickOutside={this.closePicker}>
        <HotKeys keyMap={this.keyMap} handlers={this.handlerHotkeys}>
          <div className={`input-wrapper ${styles.wrapper}`}>
            <div className="input-group">
              <input type="text"
                className="form-control"
                placeholder={!this.props.readOnly && placeholderTime}
                value={this.state.value || ''}
                onChange={this.handleInputChange}
                readOnly={this.props.readOnly}
                disabled={this.props.readOnly}
                onBlur={this.handleInputBlur}
                onFocus={this.onFocus} //workaround for a bug caused by onClickOutside()
                onKeyDown={this.onKeyDown}
              />
              <span className="input-group-btn">
                <ButtonInputGroup
                  icon={FaRegClock}
                  disabled={this.props.readOnly}
                  onClick={timeIconOnClick} />
              </span>
            </div>
          </div>
          {this.state.showClock &&
            <div className={`${styles.picker} scale-animate ${closingAnimation}`}>
              <Triangle/>
              {clockPicker}
          </div>
          }
          {this.state.emptyDateError && <Validation {...errorParams}/>}
        </HotKeys>
      </OnClickOutside>
    );
  }
}
